/* eslint-disable no-empty */
import React, { useMemo, useState } from 'react';

import {
  ALL,
  ANALYTICS_NAVIGATION_MAP,
  DashboardTiles,
  LICENSES_EXPIRING_BAR_LABELS,
  VENDORS,
} from '@common/constants';
import { DoughnutChartTile } from '@pages/InsightsAndAnalytics/tiles/DoughnutChartTile';
import styles from './styles.module.css';

import { useDashboardTileSaveMutation } from '@hooks/dashboard';
import { DashboardIcon } from '@cloudclevr/components';
import { MenuItem } from '@cloudclevr/components';
import { VendorSelection, VendorType } from '@common/types';
import {
  useLicencesActivityQuery,
  useLicencesAssignmentQuery,
  useLicencesExpiredSnapshotQuery,
  useLicencesExpiringSnapshotQuery,
  useLicencesOverassignedQuery,
  useLicencesFiltersQuery,
  useRedundantLicencesFiltersQuery,
  useLicencesRedundantQuery,
  useRedundantSnapshotLicencesFiltersQuery,
} from '@hooks/licenses';

import { SelectItem } from '@cloudclevr/components';
import { NumberTile } from '@pages/InsightsAndAnalytics/tiles/NumberTile';
import { HorizontalBarChartTile } from '@pages/InsightsAndAnalytics/tiles/HorizontalChartTile';
import {
  useHardwareInventorySnapshotQuery,
  useDeviceInventoryTypesQuery,
  useLicencesInventorySnapshotQuery,
} from '@hooks/utilisation';
import { DeviceUsage } from '@pages/Dashboard/tiles/DeviceUsage';
import { LicenseInventoryChangesOverTime } from '@pages/Dashboard/tiles/LicenseInventoryChangesOverTime';
import { MeetingRoomDeviceUsage } from '@pages/Dashboard/tiles/MeetingRoomDeviceUsage';
import { GroupsTeamsInventory, SharePointUsage } from '@pages/Dashboard/tiles';
import { LicensesFilterData } from '@hooks/licenses/types';
import useServicesStore from '@store/servicesStore';
import { getTrendData, getTrendLabels } from '@utils/index';
import { useUtilisationSummaryQuery, useSummaryApisEnabled, useFreshnessIndicatorEnabled, pickSummary } from '@hooks/summary';
import { getLatestUpdateTime, formatFreshnessTimestamp } from '@hooks/summary/freshness';
import { toLicenseInventorySnapshot, toLicensesAssignment, toLicensesActivity, toLicensesRedundant, toLicensesExpired, toHardwareInventorySnapshot, toVendorList, toDeviceTypeList, toReasonList, toLicensesExpiring, toSharePointUsageSnapshot, toDeviceUsageSnapshot, toMeetingRoomDeviceUsageSnapshot, toGroupsTeamsInventorySnapshot, toLicenseInventoryTrend } from '@hooks/summary/adapters';
import {
  LineChartTickFormat,
  LineChartTile,
  LineChartTypes,
} from '@pages/InsightsAndAnalytics/tiles/LineChartTile';

const UtilisationPage: React.FC = () => {
  const [typeList, setTypeList] = useState<string[]>([]);
  const [vendorSelection, setVendorSelection] = useState<VendorSelection>({
    assignment: ALL,
    overassigned: ALL,
    expiring: ALL,
    expired: ALL,
    inventory: ALL,
  });
  const [days, setDays] = useState('30');
  const [redundantLicenseReason, setRedundantLicenseReason] = useState('All');
  const [redundantLicenseVendor, setRedundantLicenseVendor] = useState('All');
  const [sharePointDataType, setSharePointDataType] = useState('sites');
  const [sharePointDays, setSharePointDays] = useState('30');
  const [deviceUsageDays, setDeviceUsageDays] = useState('30');
  const [meetingRoomDays, setMeetingRoomDays] = useState('30');
  const [licenseTrendDays, setLicenseTrendDays] = useState('30');
  const getVendorList = (type: string): VendorType[] => {
    return vendorSelection[type] === ALL ? VENDORS : [vendorSelection[type]];
  };

  // Summary API: single request replaces many snapshot calls when feature flag is on.
  // When summaryEnabled, legacy hooks are disabled from first render (no race condition).
  const summaryEnabled = useSummaryApisEnabled();
  const { data: summaryData, isLoading: isSummaryLoading } = useUtilisationSummaryQuery();
  const subscriptionsSummary = pickSummary(summaryData, 'Subscriptions');
  const licensesSummary = pickSummary(summaryData, 'Licenses');
  const devicesSummary = pickSummary(summaryData, 'Devices');
  const sharePointSummary = pickSummary(summaryData, 'SharePointUsage');
  const sharePointSitesSummary = pickSummary(summaryData, 'SharePointSites');
  const userAssociatedTeamsSummary = pickSummary(summaryData, 'UserAssociatedTeams');
  const teamHealthSummary = pickSummary(summaryData, 'TeamsHealth');
  const trunksSummary = pickSummary(summaryData, 'Trunks');

  const { data: filters } = useLicencesFiltersQuery(undefined, undefined, !summaryEnabled);
  const { data: overassignedLicenseFilter } = useRedundantSnapshotLicencesFiltersQuery(!summaryEnabled);

  // Legacy hooks  disabled when feature flag is on; always called for React rules of hooks.
  const legacyHooks = {
    assignment: useLicencesAssignmentQuery({
      vendorList: getVendorList('assignment'),
      enabled: !summaryEnabled,
    }),
    overassigned: useLicencesRedundantQuery({
      vendorList:
        redundantLicenseVendor === 'All'
          ? overassignedLicenseFilter?.vendorList
          : [redundantLicenseVendor],
      reasonList: redundantLicenseReason === 'All'
        ? overassignedLicenseFilter?.reasonList
        : [redundantLicenseReason],
      enabled: !summaryEnabled,
    }),
    expiring: useLicencesExpiringSnapshotQuery({
      vendorList: getVendorList('expiring'),
      enabled: !summaryEnabled,
    }),
    expired: useLicencesExpiredSnapshotQuery({
      vendorList: getVendorList('expired'),
      enabled: !summaryEnabled,
    }),
    inventory: useLicencesInventorySnapshotQuery({
      vendorList: getVendorList('inventory'),
      enabled: !summaryEnabled,
    }),
    activity: useLicencesActivityQuery({
      days,
      enabled: !summaryEnabled,
    }),
    hardware: useHardwareInventorySnapshotQuery({
      typeList: typeList ? typeList.filter(item => item !== 'all') : [],
      enabled: !summaryEnabled,
    }),
  };

  // When summary is loaded: serve data from summary, applying filters client-side.
  // While summary is loading: isLoading=true via isSummaryLoading spread.
  const dataHooks = {
    assignment: summaryEnabled
      ? { data: toLicensesAssignment(subscriptionsSummary), isLoading: isSummaryLoading }
      : legacyHooks.assignment,
    overassigned: summaryEnabled
      ? {
        data: toLicensesRedundant(
          licensesSummary,
          redundantLicenseReason === 'All' ? undefined : [redundantLicenseReason],
          redundantLicenseVendor,
        ),
        isLoading: isSummaryLoading,
      }
      : legacyHooks.overassigned,
    expiring: summaryEnabled
      ? { data: toLicensesExpiring(subscriptionsSummary), isLoading: isSummaryLoading }
      : legacyHooks.expiring,
    expired: summaryEnabled
      ? { data: toLicensesExpired(subscriptionsSummary), isLoading: isSummaryLoading }
      : legacyHooks.expired,
    inventory: summaryEnabled
      ? { data: toLicenseInventorySnapshot(subscriptionsSummary, licensesSummary, vendorSelection.inventory === ALL ? undefined : vendorSelection.inventory), isLoading: isSummaryLoading }
      : legacyHooks.inventory,
    activity: summaryEnabled
      ? { data: toLicensesActivity(licensesSummary ?? subscriptionsSummary, days), isLoading: isSummaryLoading }
      : legacyHooks.activity,
    hardware: summaryEnabled
      ? { data: toHardwareInventorySnapshot(devicesSummary, typeList?.filter(t => t !== 'all')), isLoading: isSummaryLoading }
      : legacyHooks.hardware,
  };

  const licensesExpiringBarLabels = useMemo(
    () =>
      dataHooks.expiring.data
        ? LICENSES_EXPIRING_BAR_LABELS.map(
            (label, index) =>
              `${label} ${dataHooks.expiring.data?.expirations[index].count}`
          )
        : [],
    [dataHooks.expiring.data]
  );

  const {
    data: deviceInventoryTypesData,
  } = useDeviceInventoryTypesQuery(!summaryEnabled);

  // Device types for the filter checkboxes: from summary when available, otherwise legacy API.
  // Memoized to keep a stable reference  SelectDropdown has a useEffect([items])
  // that calls setSelectedIds per item; a new array reference every render causes an infinite loop.
  const deviceTypes: string[] = useMemo(
    () => summaryEnabled && devicesSummary
      ? toDeviceTypeList(devicesSummary)
      : (deviceInventoryTypesData ?? []),
    [summaryEnabled, devicesSummary, deviceInventoryTypesData]
  );

  const deviceInventorySortBy: SelectItem[] = useMemo(
    () => [
      { id: '0', name: 'All types', value: 'all' },
      ...deviceTypes.map((item, index) => ({
        id: (index + 1).toLocaleString(),
        name: item,
        value: item,
        checked: false,
      })),
    ],
    [deviceTypes]
  );

  const checkBoxItems = useMemo(
    () => deviceInventorySortBy.map(item => ({
      ...item,
      checked: typeList.includes(item.value),
    })),
    [deviceInventorySortBy, typeList]
  );

  const dashboardSaveMutation = useDashboardTileSaveMutation();
  const handleItemAction = (type: DashboardTiles) => () => {
    const getParametersData = () => {
      switch (type) {
        case DashboardTiles.LicenseInventory:
          return {
            vendorList: getVendorList('inventory'),
          };
        case DashboardTiles.HardwareInventory:
          return {
            typeList: typeList ? typeList : [],
          };
        case DashboardTiles.LicensesExpiringSoon:
          return { vendorList: getVendorList('expiring') };
        case DashboardTiles.ExpiredAndDisabledLicenses:
          return { vendorList: getVendorList('expired') };
        case DashboardTiles.LicenseAssignment:
          return { vendorList: getVendorList('assignment') };
        case DashboardTiles.LicenseActivity:
          return { days };
        case DashboardTiles.OverassignedLicenses:
          return { vendorList: getVendorList('overassigned') };

        default:
          return {};
      }
    };

    const parametersData = getParametersData();

    dashboardSaveMutation.mutate({
      parameters: JSON.stringify(parametersData),
      tileType: type,
    });
  };


  const handleSelectChange = (value: string) => {
    try {
      const allTypesValue = 'all';
      let newTypes: string[] = [];

      if (typeList.includes(value)) {
        newTypes = value === allTypesValue ? [] :
          typeList.filter(type => type !== value && type !== allTypesValue)
      } else {

        if (value === allTypesValue) {
          newTypes = [allTypesValue, ...deviceTypes]
        } else {
          const selectedValues = [...typeList, value];
          newTypes = selectedValues.length === deviceTypes.length ?
            [...selectedValues, allTypesValue] : selectedValues
        }
      }
      setTypeList(newTypes);
    } catch { }
  };

  const handleDropdownChange = (type: keyof VendorSelection, value: string) => {
    if (type === 'activity') {
      setDays(value);
    } else if (type === 'overassigned') {
      setRedundantLicenseVendor(value)
    }
    else {
      setVendorSelection((prev) => ({
        ...prev,
        [type]: value as VendorType,
      }));
    }
  };

  const handleDropdownLicenseChange = (type: keyof VendorSelection, value: string) => {
    if (type === 'reason') {
      setDays(value);
    }
    else {
      setVendorSelection((prev) => ({
        ...prev,
        [type]: value as VendorType,
      }));
    }
  };


  const createSortVendorsMenuItems = (
    type: keyof VendorSelection
  ) => {
    const Menu = [
      {
        id: '1',
        label: 'All Vendors',
        action: () => handleDropdownChange(type, 'All'),
      },
    ];
    let vendors;
    if (summaryEnabled && subscriptionsSummary) {
      // Derive vendor list from Subscriptions summary Vendor_* keys (generated by CountByFilter attribute)
      vendors = new Set<string>(toVendorList(subscriptionsSummary));
    } else if (type === 'overassigned') {
      vendors = new Set<string>(overassignedLicenseFilter?.vendorList.map((item: string) => item));
    } else {
      vendors = new Set<string>(filters?.licenseType.map((item: LicensesFilterData) => item.vendor) ?? []);
    }
    Array.from(vendors).map((item: string) => {
      Menu.push({
        id: item,
        label: item,
        action: () => handleDropdownChange(type, item),
      });
    });
    return Menu;
  }

  const createCompareLicenseMenuItems = (
  ) => {
    const Menu = [
      {
        id: '1',
        label: 'All reasons',
        action: () => setRedundantLicenseReason('All'),
      },
    ];
    const reasons = summaryEnabled && licensesSummary
      ? new Set<string>(toReasonList(licensesSummary))
      : new Set<string>(overassignedLicenseFilter?.reasonList.map((item: string) => item));
    Array.from(reasons).map((item: string) => {
      Menu.push({
        id: item,
        label: item,
        action: () => setRedundantLicenseReason(item),
      });
    });
    return Menu;
  }


  const createMenuItems = (tileType: DashboardTiles): MenuItem[] => [
    {
      id: '0',
      label: 'Add to Dashboard',
      icon: <DashboardIcon classNames="mr-1" />,
      value: 'addToDashboard',
      action: handleItemAction(tileType),
    },
  ];

  const createSortDaysMenuItems = (type: keyof VendorSelection): MenuItem[] => [
    {
      id: '1',
      label: 'Last 30 days',
      action: () => handleDropdownChange(type, '30'),
    },
    {
      id: '2',
      label: 'Last 60 days',
      action: () => handleDropdownChange(type, '60'),
    },
    {
      id: '3',
      label: 'Last 90 days',
      action: () => handleDropdownChange(type, '90'),
    },
  ];

  const showFreshness = useFreshnessIndicatorEnabled();
  const freshnessTime = formatFreshnessTimestamp(getLatestUpdateTime(summaryData));
  const summaryLicenseTrend = toLicenseInventoryTrend(subscriptionsSummary);
  const summaryTotalTrendWindow = summaryLicenseTrend?.totalTrend.slice(-Number(licenseTrendDays)) ?? [];
  const summaryInactiveTrendWindow = summaryLicenseTrend?.inactiveTrend.slice(-Number(licenseTrendDays)) ?? [];

  return (
    <>
      {showFreshness && freshnessTime && (
        <p className="mb-2 text-right text-xs text-gray-400">Data as of {freshnessTime}</p>
      )}
      <div className={styles.pageContainer}>
        <div className={styles.pageGrid}>
          <DoughnutChartTile
            contextData={createMenuItems(DashboardTiles.LicenseInventory)}
            classNames={'col-span-6 md:col-span-6 xl:col-span-3'}
            sortData={createSortVendorsMenuItems('inventory')}
            headerTitle={'Licence inventory'}
            isLoading={dataHooks.inventory.isLoading}
            data={
              dataHooks.inventory.data
                ? Object.values(dataHooks.inventory.data.inventorySnapshot)
                : []
            }
            dataLabels={
              dataHooks.inventory.data
                ? Object.keys(dataHooks.inventory.data.inventorySnapshot)
                : []
            }
            buttonLabel={'Licence inventory data'}
            linkPath={ANALYTICS_NAVIGATION_MAP['licenseInventory']}
            isLgSize={true}
            showBracketLabelValue={true}
            data-testid="tile-container-licence-inventory"
          />

          <DoughnutChartTile
            contextData={createMenuItems(DashboardTiles.HardwareInventory)}
            classNames={'col-span-6 md:col-span-6 xl:col-span-3'}
            checkBoxItems={checkBoxItems}
            headerTitle={'Device inventory'}
            isLoading={dataHooks.hardware.isLoading}
            data={
              dataHooks.hardware.data
                ? Object.values(dataHooks.hardware.data.inventorySnapshot)
                : []
            }
            dataLabels={
              dataHooks.hardware.data
                ? Object.keys(dataHooks.hardware.data.inventorySnapshot)
                : []
            }
            handleDropdownSelect={handleSelectChange}
            buttonLabel={'Device inventory data'}
            linkPath={ANALYTICS_NAVIGATION_MAP['deviceInventory']}
            isLgSize={true}
            showBracketLabelValue={true}
            showMessageIfEmpty={true}
            emptyMessageTextAndSubtext={{
              Text: "You have no devices configured",
              SubText: "Data will appear here once devices have been configured."
            }}
            data-testid="tile-container-device-inventory"
          />

          <LicenseInventoryChangesOverTime
            classNames="col-span-full lg:col-span-6 xl:col-span-6"
            params={JSON.stringify({ days: '30' })}
          />

          <DoughnutChartTile
            contextData={createMenuItems(DashboardTiles.LicenseAssignment)}
            classNames={'col-span-6 md:col-span-3 xl:col-span-2'}
            sortData={createSortVendorsMenuItems('assignment')}
            headerTitle={'Licence assignment'}
            isLoading={dataHooks.assignment.isLoading}
            data={
              dataHooks.assignment.data
                ? Object.values(dataHooks.assignment.data)
                : []
            }
            dataLabels={['Unassigned licences', 'Assigned licences']}
            buttonLabel={'Unassigned licence data'}
            linkPath={ANALYTICS_NAVIGATION_MAP['assignment']}
            showBracketLabelValue={true}
            data-testid="tile-container-licence-assignment"
          />

          <DoughnutChartTile
            contextData={createMenuItems(DashboardTiles.LicenseActivity)}
            classNames={'col-span-6 md:col-span-3 xl:col-span-2 lg:row-start-3'}
            sortData={createSortDaysMenuItems('activity')}
            headerTitle={'Licence activity'}
            isLoading={dataHooks.activity.isLoading}
            data={dataHooks.activity.data
              ? [
                dataHooks.activity.data.inactiveLicenses,
                dataHooks.activity.data.activeLicenses,
              ]
              : []}
            dataLabels={['Inactive licenses', 'Active licenses']}
            buttonLabel={'Inactive licence data'}
            linkPath={ANALYTICS_NAVIGATION_MAP['activity']}
            showBracketLabelValue={true}
            data-testid="tile-container-licence-activity"
          />

          <NumberTile
            contextData={createMenuItems(DashboardTiles.OverassignedLicenses)}
            classNames={'col-span-6 md:col-span-3 xl:col-span-2 lg:row-start-3'}
            sortData={createSortVendorsMenuItems('overassigned')}
            compareData={createCompareLicenseMenuItems()}
            headerTitle={'Redundant licences'}
            isLoading={dataHooks.overassigned.isLoading}
            data={
              dataHooks.overassigned.data
                ? Object.values(dataHooks.overassigned.data)
                : []
            }
            isArrowColorReversed={true}
            percentageLabel=""
            buttonLabel={'Redundant licence data'}
            linkPath={ANALYTICS_NAVIGATION_MAP['overassigned']}
            data-testid="tile-container-redundant-licences"
          />

          <NumberTile
            contextData={createMenuItems(
              DashboardTiles.ExpiredAndDisabledLicenses
            )}
            classNames={'col-span-6 md:col-span-3 xl:col-span-2 lg:row-start-4'}
            sortData={createSortVendorsMenuItems('expired')}
            headerTitle={'Expired & disabled licences'}
            isLoading={dataHooks.expired.isLoading}
            data={
              dataHooks.expired.data
                ? Object.values(dataHooks.expired.data)
                : []
            }
            buttonLabel={'Expired licence data'}
            linkPath={ANALYTICS_NAVIGATION_MAP['expired']}
            data-testid="tile-container-expired-disabled-licences"
          />

          <HorizontalBarChartTile
            contextData={createMenuItems(DashboardTiles.LicensesExpiringSoon)}
            classNames={'col-span-6 xl:col-span-4 lg:row-start-4'}
            sortData={createSortVendorsMenuItems('expiring')}
            headerTitle={'Licences expiring soon'}
            isLoading={dataHooks.expiring.isLoading}
            data={
              dataHooks.expiring.data?.expirations.map((item) => item.count) ||
              []
            }
            dataLabels={licensesExpiringBarLabels}
            barBackgroundColors={[
              '#00CF6C',
              '#25A8D1',
              '#B65AD7',
              '#FFB800',
              '#54b534',
            ]}
            buttonLabel={'Licence expiry data'}
            linkPath={ANALYTICS_NAVIGATION_MAP['expiring']}
            data-testid="tile-container-licences-expiring-soon"
          />

          <SharePointUsage
            classNames="col-span-6 md:col-span-3 xl:col-span-2"
            params={JSON.stringify({ days: '30', dataType: 'sites' })}
          />
        </div>
      </div>

      <h1 className={styles.header}>Cloud storage, groups & teams</h1>
      <div className={styles.pageContainer}>
        <div className={styles.pageGrid}>
          <GroupsTeamsInventory
            classNames="col-span-6 md:col-span-3 xl:col-span-2"
            params={JSON.stringify({ vendor: '' })}
          />
        </div>
      </div>

      <h1 className={styles.header}>Devices</h1>
      <div className={styles.pageContainer}>
        <div className={styles.pageGrid}>
          <DeviceUsage
            classNames={'col-span-6 md:col-span-3 xl:col-span-2 lg:row-start-1'}
            params={JSON.stringify({ days: '30', type: '' })}
          />
          <MeetingRoomDeviceUsage
            classNames={'col-span-6 md:col-span-3 xl:col-span-2 lg:row-start-1'}
            params={JSON.stringify({ days: '30' })}
          />
        </div>
      </div>
    </>
  );
};

export default UtilisationPage;
